import {
    AlphaFeedMetricKey,
    AlphaFeedSegmentId,
    AlphaFeedPulseTraderVariant,
    AlphaFeedTradingNetwork,
} from '@/lib/types/alphafeedTypes';
import type { HlInterval } from '@/hooks/hypertracker/useHlCandles';

/** Used in disabled detail/events queries so no `undefined` path ends up in the cache key. */
export const ALPHAFEED_PLACEHOLDER_POSITION_ID = '__none__';

export const ALPHAFEED_LIMITS = {
    positions: { min: 1, max: 100, default: 20 },
    events: { min: 1, max: 500, default: 100 },
    pulse: { min: 1, max: 100, default: 20 },
};

export const ALPHAFEED_SSE_EVENT = 'alphaFeed';
export const ALPHAFEED_SSE_LIFECYCLE_EVENT = 'alphaFeedTradingLifecycle';
export const ALPHAFEED_SSE_PULSE_EVENT = 'alphaFeedPulse';
export const ALPHAFEED_SSE_REWARD_STATUS_EVENT = 'alphaFeedRewardStatus';
export const ALPHAFEED_LIFECYCLE_CLOSED_CHANGE = 'lifecycle_closed';
export const ALPHAFEED_SSE_SHUTDOWN_EVENT = 'shutdown';
/** Safe default until the backend ping interval is confirmed (none observed within 75 s). */
export const ALPHAFEED_SSE_HEARTBEAT_TIMEOUT_MS = 300_000;

export const ALPHAFEED_SSE_DEDUP_CAP = 1000;
export const ALPHAFEED_INVALIDATION_DEBOUNCE_MS = 500;
export const ALPHAFEED_INVALIDATION_MAX_WAIT_MS = 2000;
export const ALPHAFEED_RECONNECT_BASE_MS = 1000;
export const ALPHAFEED_RECONNECT_MAX_MS = 30_000;
export const ALPHAFEED_SSE_FAILURES_BEFORE_REPORT = 5;

export const SEGMENT_VARIANT: Record<AlphaFeedSegmentId, AlphaFeedPulseTraderVariant> = {
    top_scalper: 'topPerformer',
    top_day_trader: 'topPerformer',
    counter_scalper: 'spiraling',
    counter_day_trader: 'spiraling',
};

/** Metric window shown in the feed card footer, per alpha segment. */
export const SEGMENT_STATS_WINDOW: Record<AlphaFeedSegmentId, AlphaFeedMetricKey> = {
    top_scalper: '7d',
    counter_scalper: '7d',
    top_day_trader: '30d',
    counter_day_trader: '30d',
};

/** Fixed chart framing per breakpoint: total candles and min buffer before entry / after exit. */
export const ALPHAFEED_CHART_LAYOUT = {
    desktop: { candles: 70, buffer: 5 },
    mobile: { candles: 31, buffer: 3 },
};
export type AlphaFeedChartLayout = (typeof ALPHAFEED_CHART_LAYOUT)['desktop'];

/** HyperLiquid intervals, smallest first; the smallest that fits the position wins. */
export const ALPHAFEED_TIMEFRAME_LADDER: HlInterval[] = [
    '1m',
    '3m',
    '5m',
    '15m',
    '30m',
    '1h',
    '2h',
    '4h',
    '8h',
    '12h',
    '1d',
    '3d',
    '1w',
    '1M',
];
/** Empty bars between the newest candle and the price axis. */
export const ALPHAFEED_CHART_RIGHT_PADDING_BARS = 5;
export const ALPHAFEED_PRE_ENTRY_OPACITY = 0.2;
export const ALPHAFEED_TAIL_ANIMATION_MS = 400;
export const ALPHAFEED_TAIL_MIN_POLL_MS = 60_000;
/** Long candles are not re-polled; the live mid price keeps the last candle current. */
export const ALPHAFEED_TAIL_NO_POLL_INTERVALS: string[] = ['12h', '1d', '3d', '1w', '1M'];

export const ALPHAFEED_CANDLE_COLORS = {
    up: '#38CB89',
    down: '#FD6E4E',
};

export const ALPHAFEED_CHART_AXIS = {
    textColor: '#828489',
    borderColor: '#1d1d23',
};

/** Live price line / label colors while the live candle is up (down uses thunderbird primitives). */
export const ALPHAFEED_LIVE_PRICE_OPEN_COLORS = {
    line: '#38CB89',
    background: '#1f473e',
    border: '#38cb89',
    text: '#33eb8c',
};

export const ALPHAFEED_PAIR_QUOTE = 'USDC';
/** Hyperliquid coin icon CDN, `{base}/{SYMBOL}.svg`. */
export const COIN_ICON_BASE_URL = 'https://app.hyperliquid.xyz/coins';
/** Coins whose CDN icon is a dark glyph on a transparent background, unreadable on the dark theme. */
export const COIN_ICONS_WITH_LIGHT_BG = new Set([
    'CFX',
    'XRP',
    'WLD',
    'BIGTIME',
    'ADA',
    'NEAR',
    'GALA',
    'ONDO',
    'DYM',
    'AR',
    'XLM',
    'MEGA',
    'ETH',
    'io:ANTH',
]);
export const COIN_ICON_LIGHT_BG = '#fff';

/** Page-level override of HT_MAX_WIDTH_CONTENT so the feed card reaches the 800px design width. */
export const ALPHAFEED_MAX_WIDTH_CONTENT = '1350px';
export const ALPHAFEED_SIDE_RAIL_WIDTH = 40;

/** Fill marker geometry, px: circle diameter, distance from the wick, stack step (circles touch). */
export const ALPHAFEED_FILL_MARKER = {
    size: 14,
    gap: 4,
    step: 14,
    /** How far the +/− badge sticks out beyond the circle. */
    badgeOverhang: 3.5,
    /** Extra top room so sell markers on the highest candle clear the chart header. */
    headerClearance: 14,
};

/** Prod members who see the live AlphaFeed instead of the landing during the beta. */
export const ALPHAFEED_BETA_MEMBER_IDS: number[] = [1, 48916, 214345, 280891, 281816, 2461, 287013];

/** "Publicly Known Wallets" tab is hidden until the feed supports that category. */
export const ALPHAFEED_PUBLIC_WALLETS_TAB_ENABLED = false;

/** Mobile page tabs. */
export const ALPHAFEED_MOBILE_TAB = {
    EXCEPTIONAL: 'exceptional',
    PUBLIC: 'public',
    PULSE: 'pulse',
} as const;
export type AlphafeedMobileTab = (typeof ALPHAFEED_MOBILE_TAB)[keyof typeof ALPHAFEED_MOBILE_TAB];

export interface HyperliquidNetworkConfig {
    hyperliquidChain: 'Mainnet' | 'Testnet';
    infoUrl: string;
    exchangeUrl: string;
    wsUrl: string;
    /** EIP-712 domain chain (Arbitrum Sepolia) used by Hyperliquid user-signed actions. */
    signatureChainId: `0x${string}`;
    chainId: number;
}

/** Reviewed Hyperliquid parameters per backend `trading/config` network. */
export const HL_NETWORKS: Record<AlphaFeedTradingNetwork, HyperliquidNetworkConfig> = {
    testnet: {
        hyperliquidChain: 'Testnet',
        infoUrl: 'https://api.hyperliquid-testnet.xyz/info',
        exchangeUrl: 'https://api.hyperliquid-testnet.xyz/exchange',
        wsUrl: 'wss://api.hyperliquid-testnet.xyz/ws',
        signatureChainId: '0x66eee',
        chainId: 421614,
    },
    mainnet: {
        hyperliquidChain: 'Mainnet',
        infoUrl: 'https://api.hyperliquid.xyz/info',
        exchangeUrl: 'https://api.hyperliquid.xyz/exchange',
        wsUrl: 'wss://api.hyperliquid.xyz/ws',
        signatureChainId: '0x66eee',
        chainId: 421614,
    },
};

/** Trading status per wallet is reused for SPA navigation; a page reload always refetches. */
export const ALPHAFEED_TRADING_STATUS_STALE_MS = 5 * 60_000;
/** Hyperliquid `/info` may lag a just-accepted approval. */
export const ALPHAFEED_APPROVAL_CONFIRM_ATTEMPTS = 3;
export const ALPHAFEED_APPROVAL_CONFIRM_DELAY_MS = 1000;

/** Trade error toasts: auto-hide delay and how many stay stacked at once. */
export const ALPHAFEED_TOAST_DURATION_MS = 5000;
export const ALPHAFEED_TOAST_MAX_VISIBLE = 3;
